import { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { Users, FileText, FlaskConical, Layers, FileCheck2, BellRing, ShieldAlert, Download, Clock, AlertTriangle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api, { downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { PageHeader } from "@/components/shared";
import { STATUT_LABELS } from "@/lib/rbac";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <Card className="p-4 border-slate-200" data-testid={`stat-${label}`}>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs text-slate-500">{label}</div>
          <div className="font-head text-2xl font-bold text-slate-900 mt-1">{value ?? "—"}</div>
        </div>
        <div className="h-10 w-10 rounded-lg flex items-center justify-center" style={{ background: accent }}>
          <Icon className="h-5 w-5 text-white" />
        </div>
      </div>
    </Card>
  );
}

function fmtDureeShort(sec) {
  if (sec == null) return "—";
  const h = Math.round(sec / 3600);
  if (h >= 24) return `${(h / 24).toFixed(1)} j`;
  if (h >= 1) return `${h} h`;
  return `${Math.round(sec / 60)} min`;
}

function DelaiStat({ icon: Icon, label, value, tone, onClick }) {
  const tones = {
    red: "border-red-200 bg-red-50 text-red-700",
    amber: "border-amber-200 bg-amber-50 text-amber-800",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-700",
  };
  return (
    <Card onClick={onClick} data-testid={`delai-stat-${tone}`}
          className={`p-4 border ${tones[tone]} ${onClick ? "cursor-pointer hover:shadow-sm transition-shadow" : ""}`}>
      <div className="flex items-center gap-3">
        <Icon className="h-5 w-5" />
        <div>
          <div className="text-xs opacity-80">{label}</div>
          <div className="font-head text-2xl font-bold">{value ?? 0}</div>
        </div>
      </div>
    </Card>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [perf, setPerf] = useState(null);
  const [mois, setMois] = useState("");

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/stats/performance", { params: mois ? { month: mois } : {} })
      .then((r) => setPerf(r.data)).catch(() => setPerf(null));
  }, [mois]);

  const exportPerf = (fmt) => {
    const q = new URLSearchParams({ format: fmt });
    if (mois) q.set("month", mois);
    const ext = fmt === "csv" ? "csv" : "pdf";
    downloadFile(`/stats/performance/export?${q.toString()}`, `performance-${mois || "global"}.${ext}`)
      .catch(() => toast.error(t("db_export_err")));
  };

  const chartData = stats?.par_statut
    ? Object.entries(stats.par_statut).map(([k, v]) => ({ name: STATUT_LABELS[k] || k, value: v }))
    : [];

  return (
    <div data-testid="dashboard-page">
      <PageHeader title={`${t("db_hello")}, ${user?.nom?.split(" ")[0] || ""}`}
                  subtitle={t("db_subtitle")}>
        {stats && (
          <Button variant="outline" data-testid="export-stats-pdf-button"
                  onClick={() => downloadFile("/stats/pdf", "statistiques-path-intelligent.pdf").catch(() => toast.error(t("db_export_pdf_err")))}>
            <Download className="h-4 w-4 mr-1" /> {t("db_export_stats")}
          </Button>
        )}
      </PageHeader>

      <div className="present-track mb-6" data-testid="presentation-track">
        <a data-testid="presentation-btn" href="/presentation.html" target="_blank" rel="noopener"
           className="present-runner text-sm font-semibold text-sky-600 hover:text-sky-800 underline underline-offset-2">
          {t("db_presentation")}
        </a>
      </div>

      {stats && !stats.clinique && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Stat icon={Users} label={t("db_stat_users")} value={stats.utilisateurs} accent="#0F2942" />
          <Stat icon={ShieldAlert} label={t("db_stat_audit")} value={stats.evenements_audit} accent="#0284C7" />
        </div>
      )}

      {stats?.clinique && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-4">
            <Stat icon={Users} label={t("db_stat_patients")} value={stats.patients} accent="#0F2942" />
            <Stat icon={FileText} label={t("db_stat_demandes")} value={stats.demandes} accent="#0284C7" />
            <Stat icon={FlaskConical} label={t("db_stat_prelevements")} value={stats.prelevements} accent="#0D9488" />
            <Stat icon={Layers} label={t("db_stat_blocs")} value={stats.blocs} accent="#0369A1" />
            <Stat icon={Layers} label={t("db_stat_lames")} value={stats.lames} accent="#0F766E" />
            <Stat icon={FileCheck2} label={t("db_stat_cr")} value={stats.comptes_rendus_valides} accent="#059669" />
            <Stat icon={BellRing} label={t("db_stat_notif")} value={stats.notifications_critiques} accent="#DC2626" />
          </div>

          {stats.delais && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4" data-testid="delais-row">
              <DelaiStat icon={AlertTriangle} label={t("db_late")} value={stats.delais.retard} tone="red" onClick={() => navigate("/demandes")} />
              <DelaiStat icon={Clock} label={t("db_soon")} value={stats.delais.bientot} tone="amber" onClick={() => navigate("/demandes")} />
              <DelaiStat icon={FileCheck2} label={t("db_ontime")} value={stats.delais.a_temps} tone="emerald" />
            </div>
          )}

          <Card className="p-5 mt-6 border-slate-200">
            <h2 className="font-head text-lg font-semibold text-slate-800 mb-4">{t("db_by_status")}</h2>
            <div style={{ width: "100%", height: 300, minWidth: 0 }}>
              {chartData.length > 0 && (
                <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0} debounce={1}>
                  <BarChart data={chartData}>
                    <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#475569" }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#475569" }} />
                    <Tooltip />
                    <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                      {chartData.map((d) => <Cell key={d.name} fill="#0284C7" />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </Card>

          {perf && (perf.pathologistes || perf.phases) && (
            <Card className="p-5 mt-6 border-slate-200" data-testid="perf-card">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <h2 className="font-head text-lg font-semibold text-slate-800">{t("db_perf_title")}</h2>
                <div className="flex items-center gap-2">
                  <input type="month" value={mois} onChange={(e) => setMois(e.target.value)}
                         data-testid="perf-month-input"
                         className="border border-slate-200 rounded-md px-2 py-1 text-sm text-slate-700" />
                  <Button variant="outline" size="sm" data-testid="export-perf-pdf-button" onClick={() => exportPerf("pdf")}>
                    <Download className="h-4 w-4 mr-1" /> PDF
                  </Button>
                  <Button variant="outline" size="sm" data-testid="export-perf-csv-button" onClick={() => exportPerf("csv")}>
                    <Download className="h-4 w-4 mr-1" /> CSV
                  </Button>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <div className="text-xs font-semibold text-slate-500 mb-2">{t("db_perf_phase")}</div>
                  <ul className="text-sm space-y-1">
                    {(perf.phases || []).map((p) => (
                      <li key={p.statut} className="flex justify-between border-b border-slate-100 py-1" data-testid={`perf-phase-${p.statut}`}>
                        <span>{STATUT_LABELS[p.statut] || p.label || p.statut}</span>
                        <span className={p.depasse ? "font-mono text-red-600 font-semibold" : "font-mono text-slate-600"}>
                          {fmtDureeShort(p.moy_sec)}
                          {p.cible_sec != null && <span className="text-slate-400"> / {fmtDureeShort(p.cible_sec)}</span>}
                          {p.depasse && <AlertTriangle className="inline h-3.5 w-3.5 ml-1 -mt-0.5" />}
                        </span>
                      </li>
                    ))}
                    {(perf.phases || []).length === 0 && <li className="text-slate-400">{t("db_no_data")}</li>}
                  </ul>
                </div>
                <div>
                  <div className="text-xs font-semibold text-slate-500 mb-2">{t("db_perf_patho")}</div>
                  <ul className="text-sm space-y-1">
                    {(perf.pathologistes || []).map((p) => (
                      <li key={p.par} className="flex justify-between border-b border-slate-100 py-1"><span className="truncate max-w-[55%]">{p.par}</span><span className="text-slate-600">{p.valide} · {fmtDureeShort(p.moy_sec)}</span></li>
                    ))}
                    {(perf.pathologistes || []).length === 0 && <li className="text-slate-400">{t("db_no_cr")}</li>}
                  </ul>
                </div>
              </div>
            </Card>
          )}
        </>
      )}

      {stats && !stats.clinique && (
        <Card className="p-6 mt-6 border-slate-200 text-sm text-slate-600">
          {t("db_no_access_a")} <strong>{user?.role}</strong> {t("db_no_access_b")}
        </Card>
      )}
    </div>
  );
}
